<template>
  <div class="p-grid" v-if="$ability.can('write', 'Bovine')">
    <div class="p-col-12 p-lg-7">
      <AppSkeletonCardForm v-if="loadingFetch"></AppSkeletonCardForm>
      <Card v-else>
        <template v-slot:title>Dados do Bovino</template>
        <template v-slot:content>
          <ItemMetadata :item="bovine"></ItemMetadata>
          <AppForm
            ref="form"
            :rules="rules"
            :values="bovine"
            v-slot:default="{ formErrors }"
          >
            <div class="p-fluid p-formgrid p-grid">
              <div class="p-field p-col-12 p-md-6">
                <AppFormItem label="Fazenda" :error="formErrors.farmId">
                  <AutoComplete
                    v-model="bovine.farm"
                    :suggestions="farms"
                    @complete="onSearchFarm($event)"
                    @itemSelect="onSelectFarm()"
                    field="name"
                    :disabled="!$ability.can('read', 'Farm')"
                    dropdown
                  ></AutoComplete>
                </AppFormItem>
              </div>

              <div class="p-field p-col-12 p-md-6">
                <AppFormItem label="Lote/Piquete">
                  <AutoComplete
                    v-model="bovine.farmLot"
                    :suggestions="farmLots"
                    @complete="onSearchFarmLot($event)"
                    @itemSelect="onSelect()"
                    field="name"
                    dropdown
                    :disabled="!$ability.can('read', 'FarmLot')"
                  ></AutoComplete>
                </AppFormItem>
              </div>

              <div class="p-field p-col-12 p-md-4 p-xl-3">
                <AppFormItem
                  :error="formErrors.earring || earringError"
                  label="Brinco"
                >
                  <InputNumber
                    v-model="bovine.earring"
                    @blur="checkIfAlreadyExists()"
                  />
                </AppFormItem>
              </div>

              <div class="p-field p-col-12 p-md-4 p-xl-3">
                <AppFormItem :error="formErrors.typeId" label="Tipo">
                  <AutoComplete
                    v-model="bovine.type"
                    :suggestions="bovineTypes"
                    @complete="onSearchType($event)"
                    @itemSelect="onSelectType()"
                    @blur="checkIfAlreadyExists()"
                    field="name"
                    dropdown
                  ></AutoComplete>
                </AppFormItem>
              </div>

              <div class="p-field p-col-12 p-md-4 p-xl-3">
                <AppFormItem :error="formErrors.breedId" label="Raça">
                  <AutoComplete
                    v-model="bovine.breed"
                    :suggestions="breeds"
                    @complete="onSearchBreed($event)"
                    @itemSelect="onSelect()"
                    field="name"
                    dropdown
                  ></AutoComplete>
                </AppFormItem>
              </div>

              <div class="p-field p-col-12 p-md-4 p-xl-3">
                <AppFormItem label="Data de nascimento">
                  <Calendar
                    v-model="bovine.birthDate"
                    dateFormat="dd/mm/yy"
                    showIcon
                    :monthNavigator="true"
                    :yearNavigator="true"
                    :yearRange="birthDateRange"
                    :locale="$locales.calendar.ptBR"
                    :showOnFocus="false"
                    v-mask="'##/##/####'"
                  />
                </AppFormItem>
              </div>

              <div class="p-field p-col-12 p-md-4 p-xl-3">
                <AppFormItem label="Peso (Kg)">
                  <InputNumber
                    v-model="bovine.weight"
                    mode="decimal"
                    :minFractionDigits="2"
                  />
                </AppFormItem>
              </div>

              <div class="p-field p-col-12 p-md-4 p-xl-3">
                <BovineEditStatus
                  :id="Number($route.params.id) || null"
                  v-model:status="bovine.status"
                  v-model:statusId="bovine.statusId"
                  v-model:typeId="bovine.typeId"
                  v-model:childBirth="bovine.childBirth"
                  :services="services"
                ></BovineEditStatus>
              </div>

              <div class="p-field p-col-12 p-md-4 p-xl-3">
                <AppFormItem :error="formErrors.bovineRootId" label="Matriz">
                  <AutoComplete
                    v-model="bovine.bovineRoot"
                    :suggestions="bovineRoots"
                    @complete="onSearchRoot($event)"
                    @itemSelect="onSelectBovineRoot()"
                    field="earring"
                    dropdown
                  ></AutoComplete>
                </AppFormItem>
              </div>

              <div class="p-field p-col-12 p-md-4 p-xl-3">
                <AppFormItem label="Proprietário">
                  <AutoComplete
                    :disabled="!$ability.can('read', 'Owner')"
                    v-model="bovine.owner"
                    :suggestions="owners"
                    @complete="onSearchOwner($event)"
                    @itemSelect="onSelect()"
                    field="name"
                    dropdown
                  ></AutoComplete>
                </AppFormItem>
              </div>

              <div class="p-field p-col-12 p-md-4 p-xl-3">
                <AppFormItem label="Touro (pai)">
                  <AutoComplete
                    v-model="bovine.bullType"
                    :suggestions="bullTypes"
                    @complete="onSearchBull($event)"
                    @itemSelect="onSelect()"
                    field="name"
                    dropdown
                  ></AutoComplete>
                </AppFormItem>
              </div>

              <div class="p-field p-col-12 p-md-4 p-xl-3">
                <AppFormItem label="Cor">
                  <InputText v-model="bovine.color" />
                </AppFormItem>
              </div>

              <div class="p-field p-col-12 p-md-4 p-xl-3">
                <AppFormItem label="Valor de compra">
                  <InputNumber
                    v-model="bovine.buyValue"
                    :minFractionDigits="2"
                    mode="currency"
                    currency="BRL"
                    locale="pt-BR"
                  />
                </AppFormItem>
              </div>

              <div
                class="p-field p-col-12 p-md-4 p-xl-3"
                v-if="isTypeBovineRoot(bovine.typeId)"
              >
                <AppFormItem label="Previsão de parto">
                  <Calendar
                    v-model="bovine.childBirth"
                    dateFormat="dd/mm/yy"
                    showIcon
                    :monthNavigator="true"
                    :yearNavigator="true"
                    :yearRange="birthDateRange"
                    :locale="$locales.calendar.ptBR"
                    :showOnFocus="false"
                    v-mask="'##/##/####'"
                  />
                </AppFormItem>
              </div>
              <div class="p-field p-col-12 p-md-12">
                <AppFormItem label="Etiquetas">
                  <TagAutocomplete
                    v-model="bovine.tags"
                    :typeId="1"
                    :isVetCustomer="!!vetCustomer"
                  ></TagAutocomplete>
                </AppFormItem>
              </div>
              <div class="p-field p-col-12 p-md-12">
                <AppFormItem label="Observações">
                  <Textarea v-model="bovine.observation" rows="3" />
                </AppFormItem>
              </div>
            </div>
          </AppForm>
        </template>

        <template v-slot:footer>
          <div class="p-grid p-jc-between">
            <div style="padding-left: 8px">
              <Button
                v-if="$route.params.id"
                label="Excluir"
                class="p-button-outlined p-button-danger"
                icon="pi pi-trash"
                :disabled="!$ability.can('delete', 'Bovine')"
                @click="deleteConfirmVisible = true"
              />
            </div>
            <div style="padding-right: 8px">
              <Button
                label="Cancelar"
                class="p-button-outlined p-mr-2"
                @click="redirectToList()"
              />
              <AppButton label="Salvar" @click="save" :loading="loadingSave" />
            </div>
          </div>
        </template>
      </Card>
    </div>
    <div class="p-col-12 p-md-6 p-lg-5" v-if="!$route.params.id">
      <BovineLastInsertedCard />
    </div>
    <div class="p-col-12 p-md-6 p-lg-5" v-if="$route.params.id">
      <BovineHistoryCard style="height: 100%" :id="Number($route.params.id)" />
    </div>
    <div class="p-col-12" v-if="showCalvesCard">
      <BovineCalvesCard :id="Number($route.params.id)" />
    </div>
    <div
      class="p-col-12"
      v-if="$route.params.id && $ability.can('read', 'Service')"
    >
      <BovineServicesCard
        :id="Number($route.params.id)"
        @load="services = $event"
      />
    </div>
    <div class="p-col-12">
      <div class="p-grid p-jc-end p-mt-2">
        <router-link tag="span" :to="{ name: 'bovine.list' }">
          <Button label="Voltar" class="p-button-outlined p-mr-2" />
        </router-link>
      </div>
    </div>
    <AppModalDelete
      type="Bovino"
      :visible="deleteConfirmVisible"
      :item="`Bovino ${bovine.earring} - ${bovine?.type?.name}`"
      @confirm="onDelete()"
      @cancel="deleteConfirmVisible = false"
    ></AppModalDelete>
  </div>
  <AppPermissionAlert v-else />
</template>

<script>
import {
  bovineRootTypes,
  bovineTypes,
  isTypeBovineRoot,
} from '../../lib/bovines';
import { mapGetters } from 'vuex';
import { delay } from '../../lib/helpers';
import {
  BreedApi,
  BovineApi,
  FarmApi,
  FarmLotApi,
  OwnerApi,
  BullApi,
  ServiceApi,
} from '../../services/api';
import BovineLastInsertedCard from './components/BovineLastInsertedCard';
import BovineHistoryCard from './components/BovineHistoryCard';
import BovineCalvesCard from './components/BovineCalvesCard';
import BovineServicesCard from './components/BovineServicesCard';
import TagAutocomplete from '../../components/TagAutocomplete.vue';
import BovineEditStatus from './components/BovineEditStatus.vue';
import ItemMetadata from '../../components/ItemMetadata.vue';

export default {
  components: {
    ItemMetadata,
    BovineLastInsertedCard,
    BovineHistoryCard,
    BovineCalvesCard,
    BovineServicesCard,
    TagAutocomplete,
    BovineEditStatus,
  },

  data() {
    return {
      breeds: [],
      owners: [],
      farms: [],
      farmLots: [],
      bovineRoots: [],
      bullTypes: [],
      bovineTypes: [],
      earringError: null,
      sort: {
        order: 'asc',
        orderField: 'name',
      },
      bovine: {
        earring: null,
        typeId: null,
        statusId: 1,
        bovineRootId: null,
        breedId: '',
        weight: null,
        buyValue: null,
        observation: '',
        birthDate: '',
        type: null,
        status: null,
        breed: null,
        farm: null,
        farmLot: null,
        bovineRoot: {
          id: null,
          earring: 'Nenhuma',
        },
        bullType: null,
        tags: [],
      },
      loadingFetch: false,
      loadingSave: false,
      birthDateRange: `${this.$dayjs()
        .subtract(10, 'y')
        .format('YYYY')}:${this.$dayjs().add(10, 'y').format('YYYY')}`,
      rules: {
        earring: [{ required: true, message: 'Informe o brinco.' }],
        typeId: [{ required: true, message: 'Informe o tipo de bovino.' }],
        farmId: [{ required: true, message: 'Informe a fazenda.' }],
      },
      deleteConfirmVisible: false,
      bovineTypesIds: bovineTypes,
      services: []
    };
  },

  computed: {
    ...mapGetters('app', ['vetCustomer']),
    showCalvesCard() {
      return this.$route.params.id && isTypeBovineRoot(this.bovine.typeId);
    },
  },

  async created() {
    if (!this.$route.params.id) {
      return;
    }
    try {
      this.loadingFetch = true;
      const bovine = await BovineApi.fetchById(this.$route.params.id);
      this.bovine = {
        ...bovine,
        birthDate: bovine.birthDate ? new Date(bovine.birthDate) : null,
        childBirth: bovine.childBirth ? new Date(bovine.childBirth) : null,
        bovineRoot: bovine.bovineRoot || this.bovine.bovineRoot,
        bullType: bovine.bullType || this.bovine.bullType,
      };
    } finally {
      this.loadingFetch = false;
    }
  },

  methods: {
    isTypeBovineRoot,
    async onSearchType({ query: name }) {
      const { results: bovineTypes } = await BovineApi.fetchTypes({
        ...this.sort,
        name,
      });
      this.bovineTypes = bovineTypes;
    },
    async onSearchBreed({ query: name }) {
      const { results: breeds } = await BreedApi.fetch({
        ...this.sort,
        name,
      });
      this.breeds = breeds;
    },
    async onSearchRoot({ query: earring }) {
      if (!this.bovine.farmId) {
        this.bovineRoots = [{ id: null, earring: 'Nenhuma' }];
        return;
      }
      const query = {
        typeId: bovineRootTypes,
        orderField: 'earring',
        order: 'asc',
        available: true,
        include: '[farmLot]',
        includeFields: '[id,earring]',
        page: 'all',
        farmId: this.bovine.farmId,
        earring: earring || undefined,
      };
      const fetchResponse = await BovineApi.fetch(query);
      const bovineRoots = fetchResponse.results
        .filter((bovine) => bovine.id !== Number(this.$route.params.id))
        .map((bovine) => {
          const earring = bovine.farmLot
            ? `${bovine.earring} (Lote ${bovine.farmLot.name})`
            : bovine.earring;
          return { ...bovine, earring };
        });
      this.bovineRoots = [{ id: null, earring: 'Nenhuma' }, ...bovineRoots];
    },
    async onSearchBull({ query: name }) {
      const query = {
        orderField: 'name',
        order: 'asc',
        page: 'all',
        name: name || undefined,
      };
      const fetchResponse = await BullApi.fetch(query);
      this.bullTypes = [...fetchResponse.results];
    },
    async onSearchFarm({ query: name }) {
      const { results } = await FarmApi.fetch({
        ...this.sort,
        name,
        status: true,
      });
      this.farms = results;
    },
    async onSearchOwner({ query: name }) {
      const { results } = await OwnerApi.fetch({ ...this.sort, name });
      this.owners = results;
    },

    async onSearchFarmLot({ query: name }) {
      if (!this.bovine?.farm?.id) {
        this.farmLots = [];
      }
      const { results } = await FarmLotApi.fetch({
        ...this.sort,
        name,
        farmId: this.bovine.farm.id,
      });
      this.farmLots = results;
    },

    onSelectFarm() {
      this.bovine.farmLot = null;
      this.bovine.lotId = null;
      this.bovine.bovineRoot = null;
      this.bovineRoots = [{ id: null, earring: 'Nenhuma' }];
      this.onSelect();
    },

    async autocompleteBullType() {
      if (!this.bovine.bovineRootId) {
        return;
      }
      if (!this.bullTypes.length) {
        await this.onSearchBull({});
      }
      const serviceResponse = await ServiceApi.fetch({
        bovineId: this.bovine.bovineRootId,
      });
      const bovineRootOnService = (
        serviceResponse.results?.[0]?.bovines || []
      ).find((b) => b.bovineId === this.bovine.bovineRootId);
      const bullType = this.bullTypes.find(
        (b) => b.id === bovineRootOnService?.bullTypeId
      );
      this.bovine.bullTypeId = bullType?.id || null;
      this.bovine.bullType = bullType || null;
    },

    onSelectBovineRoot() {
      if (!this.bovine.bovineRoot) {
        this.onSelect();
      }
      if (typeof this.bovine.bovineRoot.earring === 'string') {
        const earring = this.bovine.bovineRoot.earring.split(' ')[0];
        this.bovine.bovineRoot.earring = earring;
      }
      if (this.bovine.bovineRoot.farmLot && !this.bovine.farmLot) {
        this.bovine.farmLot = this.bovine.bovineRoot.farmLot;
      }
      this.onSelect();
      this.autocompleteBullType();
    },

    onSelectType() {
      this.onSelect();
    },

    onSelect() {
      const getId = (data) => (data && data.id) || null;
      this.bovine = {
        ...this.bovine,
        typeId: getId(this.bovine.type),
        breedId: getId(this.bovine.breed),
        bovineRootId: getId(this.bovine.bovineRoot),
        bullTypeId: getId(this.bovine.bullType),
        farmId: getId(this.bovine.farm),
        lotId: getId(this.bovine.farmLot),
        ownerId: getId(this.bovine.owner),
      };
    },

    async save() {
      try {
        this.loadingSave = true;
        await this.checkIfAlreadyExists();

        if (!(await this.$refs.form.validate()) || this.earringError) {
          return;
        }

        const body = {
          ...this.bovine,
          bovineRoot: undefined,
          breed: undefined,
          owner: undefined,
          type: undefined,
          bullType:
            typeof this.bovine.bullType === 'string'
              ? this.bovine.bullType
              : undefined,
          bullTypeId: this.bovine.bullType ? this.bovine.bullTypeId : null,
          breedId: this.bovine.breed ? this.bovine.breedId : null,
        };
        console.log('body', JSON.stringify(body, null, 2));

        if (!this.$route.params.id) {
          await BovineApi.create(body);
        } else {
          await BovineApi.update(this.$route.params.id, body);
        }

        this.redirectToList();
      } catch (error) {
        console.log('error', error);
        this.handleError(error);
      } finally {
        this.loadingSave = false;
      }
    },
    redirectToList() {
      const params = {};
      if (this.$route.params.id) {
        params.cacheFilter = true;
      }
      this.$router.push({
        name: 'bovine.list',
        params,
      });
    },
    handleError(error) {
      console.log(error);
      let detail = 'Não foi possível salvar o bovino.';

      if (error.code === 'AlreadyExistError') {
        detail = 'Já existe um cadastro do mesmo tipo e brinco.';
      }

      if (error.code === 'PlanLimitExceededError') {
        detail = 'O limite de bovinos do seu plano foi atingido.';
      }

      this.$toast.add({
        severity: 'error',
        summary: 'Desculpe! Não é possível salvar.',
        detail,
        life: 3000,
      });
    },

    async checkIfAlreadyExists() {
      try {
        await delay(25);
        this.earringError = null;

        if (
          !this.bovine.typeId ||
          !this.bovine.earring ||
          !this.bovine.farmId
        ) {
          return;
        }

        const { results: bovines } = await BovineApi.fetch({
          include: '[]',
          typeId: isTypeBovineRoot(this.bovine.typeId)
            ? bovineRootTypes
            : this.bovine.typeId,
          earring: this.bovine.earring,
          farmId: this.bovine.farmId,
          available: true,
        });

        const isFromEditing =
          bovines.length && Number(this.$route.params.id) === bovines[0].id;

        if (bovines.length && !isFromEditing) {
          this.earringError = {
            message: 'Esse brinco já foi cadastrado.',
          };
        }
      } catch (error) {
        console.log(error);
      }
    },

    async onDelete() {
      try {
        await BovineApi.delete(this.$route.params.id);
        this.$router.push({ name: 'bovine.list' });
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Desculpe!',
          detail: 'Não foi possível excluir o bovino.',
          life: 3000,
        });
      } finally {
        this.deleteConfirmVisible = false;
      }
    },
  },
};
</script>

<style>
</style>